import { DateTime } from "luxon";
import {
  WEEK_PRESETS,
  getPresetRange,
  getWeekBounds,
  toApiDates,
} from "@/components/common/WeekPicker/weekPicker.utils";
import { getDefaultRange } from "@/components/projectCommon/DatePeriod/utils";

/** Day preset → its week-aligned counterpart. */
export const DAY_TO_WEEK_PERIOD: Record<string, string> = {
  L7D: "L1W",
  L30D: "L4W",
  L90D: "L12W",
  L180D: "L24W",
  YTD: "YTDW",
};

export const WEEK_TO_DAY_PERIOD: Record<string, string> = Object.fromEntries(
  Object.entries(DAY_TO_WEEK_PERIOD).map(([day, week]) => [week, day]),
);

export type ConvertedPeriod = {
  periodType: string;
  startDate: string;
  endDate: string;
};

const toJSDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toJSDate() : null;

/**
 * Maps a preset to the equivalent preset of the target picker granularity
 * (e.g. L30D ↔ L4W) with freshly computed dates. Returns null when the period
 * already fits the granularity or has no counterpart (CUSTOM, DRR).
 */
export function convertPeriodForGranularity(
  periodType: string | null | undefined,
  granularity: "DAY" | "WEEK" | undefined,
  bounds?: { min?: string | null; max?: string | null },
): ConvertedPeriod | null {
  if (!periodType) return null;

  if (granularity === "WEEK") {
    const preset = WEEK_PRESETS.find(
      (p) => p.id === DAY_TO_WEEK_PERIOD[periodType],
    );
    if (!preset) return null;
    const minDate = toJSDate(bounds?.min);
    const range = getPresetRange(
      preset.weeks,
      getWeekBounds({ minDate, maxDate: toJSDate(bounds?.max) }),
    );
    if (!range) return null;
    return { periodType: preset.id, ...toApiDates(range, minDate) };
  }

  const dayType = WEEK_TO_DAY_PERIOD[periodType];
  if (!dayType) return null;
  const range = getDefaultRange(dayType);
  return {
    periodType: dayType,
    startDate: DateTime.fromJSDate(range.startDate).toFormat("yyyy-MM-dd"),
    endDate: DateTime.fromJSDate(range.endDate).toFormat("yyyy-MM-dd"),
  };
}
